import React from 'react';

import { WidgetView, WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';

import { useBaseTable } from 'hooks/useBaseTable';
import { useBaseWidget } from 'hooks/useBaseWidget';

import { useConfigurationStore } from 'store/configurationsStore';

import { ITopOverdueData } from 'api/mutations/extractDelayDriverCsv.mutation';

import { ColumnsManager } from 'components/common/ColumnsManager/ColumnsManager';
import ErrorBoundary from 'components/common/ErrorBoundary/ErrorBoundary';
import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import NoData from 'components/common/NoData/noData';
import { TableV8 } from 'components/common/Tables/TableV8/tableV8';
import { TableContainer } from 'components/common/Tables/components/TableContainer/TableContainer';
import { TooltipIcon } from 'components/common/TooltipIcon/tooltipIcon';
import { WidgetTitle } from 'components/common/WidgetTitle/WidgetTitle';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';
import widgetWithTitleClasses from 'components/common/WidgetWithTitle/WidgetWithTitle.module.scss';

import { CriticalAnalysisDelayDriversFilter } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/ProgramCriticalAnalysis/criticalAnalysisDelayDriversFilter';
import { AddWidget } from 'components/Dashboards/Project/Components/CustomDashboard/AddRemoveWidget/addWidget';
import { IRenderCriticalAnalysisDelayDrivers } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { AddToReport } from 'components/Dashboards/Reporting/AddToReport/AddToReport';
import { ReportingVersionAndPills } from 'components/Dashboards/Reporting/ReportingVersionAndPills/ReportingVersionAndPills';
import { Narrative } from 'components/Dashboards/Reporting/WidgetNarrativeWrapper/Narrative';
import { PinboardPills } from 'components/Dashboards/Reporting/pinboardPills/PinboardPills';
import { ThreeDotsMenu } from 'components/Dashboards/Reporting/threeDotsMenu/ThreeDotsMenu';
import { useRenderCriticalAnalysisDelayDriversLogic } from 'components/widgets/CriticalActivitiesAndDelayDrivers/RenderCriticalAnalysisDelayDrivers.logic';
import { useCriticalActivitiesAndDelayDriversProvider } from 'components/widgets/CriticalActivitiesAndDelayDrivers/RenderCriticalAnalysisDelayDrivers.provider';
import {
    columnsV8,
    initialState,
} from 'components/widgets/CriticalActivitiesAndDelayDrivers/RenderCriticalAnalysisDelayDrivers.utils';
import classesGeneralWidget from 'components/widgets/Utils/Styles/widget.module.scss';

export const CriticalActivitiesAndDelayDrivers = ({
    projectId,
    contractId,
    widgetId,
    latestVersionId,
    globalFilterData,
    externalComponents = null,
    editNarrative = null,
    setSubTitleForExport,
    localFilters,

    reportWidgetRef = null,
    widgetSelectedProjectAndContract = null,
    widgetViewMode = WidgetView.page,
    title: propsTitle,
    setIsPayloadReady,
}: IRenderCriticalAnalysisDelayDrivers) => {
    const {
        componentKey,
        routeId: id,
        endpoints,
        name: configTitle,
    } = useConfigurationStore().topCriticalActivitiesDelayDrivers;

    const { table } = useBaseTable({
        widgetId,
        localFilters,
        componentKey,
        initialState,
        columns: columnsV8,
        widgetViewMode,
    });

    const { ui, api } = useCriticalActivitiesAndDelayDriversProvider({
        widgetId,
        latestVersionId,
        contractId,
        projectId,
        globalFilterData,
        localFilters,
        table,
        componentKey,
        widgetViewMode,
        endpoints,
    });

    const { subTitle, route, setEditableTitle, editableTitle } = useBaseWidget({
        id,
        widgetId,
        projectId,
        contractId,
        latestVersionId,
        title: propsTitle ?? configTitle,
        setSubTitleForExport,
        ui,
        reportWidgetRef,
        onAddWidgetCallback: table.setWidgetColumns,
        apiPayload: api.getDelayedDrivers.payload,
        widgetEndpoint: api.getDelayedDrivers.endpoint,
    });

    const { logic } = useRenderCriticalAnalysisDelayDriversLogic({
        title: propsTitle ?? configTitle,
        subTitle,
        api,
        ui,
        table,
        widgetViewMode,
        setIsPayloadReady,
    });

    return (
        <ErrorBoundary>
            <WidgetWithTitle
                    id={id}
                    title={
                        <WidgetTitle
                            title={editableTitle}
                            setTitle={setEditableTitle}
                            widgetViewMode={widgetViewMode}
                            postfix={logic.counter}
                            showPostfixInEditMode={true}
                        />
                    }
                    className={WidgetViewModeUtils.isReportView(widgetViewMode) ? widgetWithTitleClasses.inReport : ''}
                    titleFilters={[
                        <ReportingVersionAndPills
                            key={'ReportingVersionAndPills'}
                            selectedProjectAndContract={widgetSelectedProjectAndContract}
                            pills={ui.pills}
                            widgetViewMode={widgetViewMode}
                        />,
                        <Narrative key={'narrative'} editNarrative={editNarrative} />,
                        <PinboardPills
                            key={'pinboardPills'}
                            isInPinboard={ui.isInPinboard}
                            pills={ui.pills}
                            widgetViewMode={widgetViewMode}
                        />,
                        WidgetViewModeUtils.isPageOrReportAddOrEditWidget(widgetViewMode) && (
                            <CriticalAnalysisDelayDriversFilter
                                key={'filter'}
                                handleOnSearch={logic.handleOnSearchSubmitted}
                                handleDateFilter={logic.handleDate}
                                months={logic.months}
                                dateFilter={ui.state.dateFilter}
                                handleStatusType={logic.handleStatusType}
                                statusFilter={ui.state.statusFilter}
                                handleStatus={logic.handleStatus}
                                typeFilter={ui.state.typeFilter}
                                handleCriticalPath={logic.handleCriticalPath}
                                isCriticalPath={ui.state.isCriticalPath}
                                handleDelayDriver={logic.handleDelayDriver}
                                isDelayDriver={ui.state.isDelayDriver}
                            />
                        ),
                    ]}
                    titleComponents={[
                        externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                        <AddWidget
                            key={'AddWidget'}
                            componentKey={componentKey}
                            title={propsTitle ?? configTitle}
                            projectId={ui.state.projectId}
                            contractId={ui.state.contractId}
                            widgetId={widgetId}
                            route={route}
                            localFilters={ui.state}
                            callBack={table.setWidgetColumns}
                            shouldHide={true}
                            shouldUseNewIcon={true}
                        />,
                        <AddToReport
                            key={'addWidgetToReport'}
                            widgetId={widgetId}
                            componentKey={componentKey}
                            projectId={projectId}
                            contractId={contractId}
                            uiState={ui.state}
                            shouldHide={WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode)}
                            callBack={table.setWidgetColumns}
                            apiPayload={api.getDelayedDrivers.payload}
                            widgetEndpoint={api.getDelayedDrivers.endpoint}
                        />,
                        !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                            <ExportDropdown
                                key={'export'}
                                title={propsTitle ?? configTitle}
                                subTitle={subTitle}
                                handleAPICsvExport={logic.handleAPICsvExport}
                            />
                        ),
                        !WidgetViewModeUtils.isReportView(widgetViewMode) && (
                            <ColumnsManager
                                onOpenModal={() => table.setModalColumnsOpen(true)}
                                onHideModal={() => table.setModalColumnsOpen(false)}
                                showModal={table.modalColumnsOpen}
                                widgetId={widgetId}
                                componentKey={componentKey}
                                tableInstance={table.tableInstance}
                                initialState={initialState}
                                columns={columnsV8}
                                widgetViewMode={widgetViewMode}
                            />
                        ),
                        <TooltipIcon key={'info'} tooltip={api.getDelayedDrivers.instance.data?.info} />,
                        !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                            <ThreeDotsMenu
                                widgetViewMode={widgetViewMode}
                                widgetId={widgetId}
                                componentKey={componentKey}
                                shareTitle={propsTitle ?? configTitle}
                                shareLink={route}
                                projectId={projectId}
                                contractId={contractId}
                            />
                        ),
                    ]}
                >
                    <TableContainer>
                        {table.updatedInitialState && (
                            <TableV8<ITopOverdueData>
                                data={logic.filteredRows}
                                columns={columnsV8}
                                isLoading={
                                    api.getDelayedDrivers.instance.isLoading ||
                                    api.getDelayedDrivers.instance.isFetching
                                }
                                noData={<NoData>No activities match the applied filter</NoData>}
                                sorting={table.sorting}
                                setSorting={table.setSorting}
                                columnVisibility={table.columnVisibility}
                                setColumnVisibility={table.setColumnVisibility}
                                columnOrder={table.columnOrder}
                                setColumnOrder={table.setColumnOrder}
                                rowSelection={table.rowSelection}
                                setRowSelection={table.setRowSelection}
                                tableClassName={
                                    WidgetViewModeUtils.isReportView(widgetViewMode)
                                        ? classesGeneralWidget.reportTableScrollable
                                        : classesGeneralWidget.pageTableScrollable
                                }
                                wrapperClassName={
                                    WidgetViewModeUtils.isInAnyReportMode(widgetViewMode)
                                        ? classesGeneralWidget.reportTableWrapper
                                        : ''
                                }
                                pagination={table.pagination}
                                totalPages={table.totalPages}
                                setPagination={table.setPagination}
                                isPaginationDisabled={api.getDelayedDrivers.instance.isFetching}
                                displayPaginationBottomBar
                            />
                        )}
                    </TableContainer>
                </WidgetWithTitle>
        </ErrorBoundary>
    );
};
